/*
 * The crew's own day: their visits for one day, in order. priceCents is taken out for crew without the See job prices
 * tick (the server's scrub), and crew notes come in Spanish when the person's language is Spanish and a Spanish note is set.
 */
import { z } from "zod";
import { VISIT_STATUSES } from "./jobs.zod.js";

export const myVisit = z.object({
  ref: z.string(),
  date: z.string(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  status: z.enum(VISIT_STATUSES),
  jobName: z.string(),
  clientName: z.string(),
  address: z.string().nullable(),
  crewNotes: z.string().nullable(),
  crew: z.array(z.string()),
  priceCents: z.number().optional(),
});
export type MyVisit = z.infer<typeof myVisit>;

/**
 * One of my visits on the phone. No prices (priceCents only with the See job prices tick), no office notes, only notes the
 * crew may see. work is what to do (the lines' names, never their prices). Checklist labels are in the person's language.
 */
export const myVisitDetail = myVisit.extend({
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  work: z.array(z.string()),
  /** What to expect at the home: what it was priced for ("3 bed, 2 bath, 2 more rooms", "1,200 sq ft"), else its size. */
  home: z.string().nullable().default(null),
  /** The client's phone, for the crew's Call button (owner, Oct 31: calling is fine, texting the client is not for now). */
  clientPhone: z.string().nullable().default(null),
  checklist: z
    .object({
      name: z.string(),
      items: z.array(
        z.object({
          id: z.uuid(),
          label: z.string(),
          done: z.boolean(),
          /** Bedroom 2, Bathroom 1 for an item done once per room; the room for room-by-room lists. */
          room: z.string().nullable(),
          /** Which bedroom or bathroom, sent back with the tick. */
          copy: z.number(),
          /** Done before Complete. */
          must: z.boolean(),
        }),
      ),
      photosNeeded: z.number(),
      photosTaken: z.number(),
    })
    .nullable(),
  timerStartedAt: z.string().nullable(),
  timerMinutes: z.number(),
  notes: z.array(z.object({ id: z.uuid(), body: z.string(), authorName: z.string().nullable(), createdAt: z.string() })),
  /** mine: this person took it, so they can delete it (owner, Oct 31). */
  photos: z.array(
    z.object({ id: z.uuid(), name: z.string(), createdAt: z.string(), room: z.string().nullable(), mine: z.boolean() }),
  ),
  completedAt: z.string().nullable(),
  /** Set when the crew said the client paid them. */
  crewCollected: z.enum(["cash", "check", "company"]).nullable(),
  /** The brand has crew money on: Complete asks "Did the client pay you?" (Yes, Company check, No), never an amount. Named so the crew price scrub keeps it. */
  asksCashOrCheck: z.boolean(),
});
export type MyVisitDetail = z.infer<typeof myVisitDetail>;

/** A crew note from the phone. It is always a note the crew can see. */
export const myNoteBody = z.object({ body: z.string().trim().min(1, "Type the note.").max(5000) }).strict();

export const myDayQuery = z.object({
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});
